/**
 * Compliance Report Tab — per-supplier declaration compliance overview
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Typography,
  Tooltip,
} from '@mui/material';
import { PictureAsPdf as PdfIcon, TableChart as ExcelIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getComplianceReport,
  exportComplianceReportPdf,
  exportComplianceReportExcel,
  SupplierComplianceReport,
} from '../../../services/supplierDeclarationService';

const scoreColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
};

const scoreLabel = (score: number) => {
  if (score >= 80) return 'Good';
  if (score >= 50) return 'At Risk';
  return 'Critical';
};

const ComplianceReportTab: React.FC = () => {
  const [report, setReport] = useState<SupplierComplianceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  useEffect(() => {
    getComplianceReport()
      .then(setReport)
      .catch((err: any) => setError(err.response?.data?.detail || 'Failed to load compliance report'))
      .finally(() => setLoading(false));
  }, []);

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      await exportComplianceReportPdf();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    try {
      await exportComplianceReportExcel();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  const total = report.length;
  const good = report.filter(r => r.compliance_score >= 80).length;
  const atRisk = report.filter(r => r.compliance_score >= 50 && r.compliance_score < 80).length;
  const critical = report.filter(r => r.compliance_score < 50).length;

  return (
    <Box>
      {/* Summary cards */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
            <Typography variant="h4" fontWeight={700}>{total}</Typography>
            <Typography variant="body2" color="text.secondary">Total Suppliers</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center', borderTop: '3px solid #4caf50' }}>
            <Typography variant="h4" fontWeight={700} color="success.main">{good}</Typography>
            <Typography variant="body2" color="text.secondary">Good (≥80%)</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center', borderTop: '3px solid #ff9800' }}>
            <Typography variant="h4" fontWeight={700} color="warning.main">{atRisk}</Typography>
            <Typography variant="body2" color="text.secondary">At Risk (50–79%)</Typography>
          </Paper>
          <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center', borderTop: '3px solid #f44336' }}>
            <Typography variant="h4" fontWeight={700} color="error.main">{critical}</Typography>
            <Typography variant="body2" color="text.secondary">Critical (&lt;50%)</Typography>
          </Paper>
        </Box>

        {report.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={exportingPdf ? <CircularProgress size={14} /> : <PdfIcon />}
              onClick={handleExportPdf}
              disabled={exportingPdf}
            >
              Export PDF
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={exportingExcel ? <CircularProgress size={14} /> : <ExcelIcon />}
              onClick={handleExportExcel}
              disabled={exportingExcel}
            >
              Export Excel
            </Button>
          </Box>
        )}
      </Box>

      {report.length === 0 ? (
        <Alert severity="info">No suppliers found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Supplier</TableCell>
                <TableCell>Email</TableCell>
                <TableCell sx={{ minWidth: 180 }}>Compliance Score</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Total</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="center">Expiring</TableCell>
                <TableCell align="center">Expired</TableCell>
                <TableCell align="center">Pending</TableCell>
                <TableCell align="center">Requested</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {report.map((row) => (
                <TableRow key={row.supplier_id}>
                  <TableCell>
                    <Typography fontWeight={600}>{row.supplier_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {row.supplier_email?.endsWith('.supplier') ? '—' : row.supplier_email}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={row.compliance_score}
                          color={scoreColor(row.compliance_score)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 36 }}>
                        {row.compliance_score}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={scoreLabel(row.compliance_score)}
                      color={scoreColor(row.compliance_score)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">{row.total_declarations}</TableCell>
                  <TableCell align="center">
                    <Typography color="success.main" fontWeight={600}>{row.active}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="warning.main" fontWeight={600}>{row.expiring_soon}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography color="error.main" fontWeight={600}>{row.expired}</Typography>
                  </TableCell>
                  <TableCell align="center">{row.pending}</TableCell>
                  <TableCell align="center">{row.requested}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ComplianceReportTab;
